import React, { useState } from 'react';
import './CaseDataForm.css';
import { submitToSoftPro } from '../services/softproApi';
import { searchPropertyByAPN, isConfigured, setCredentials, getCredentials } from '../services/datatraceApi';
import { createTransactionRecord, saveTransaction } from '../services/datatraceTransactions';
import { useTaxSearch } from '../hooks/useTaxSearch';
import { useTitleSearch } from '../hooks/useTitleSearch';

// Import extracted constants
import {
  AI_PROVIDERS,
  GOVERNMENT_CONTACTS,
  ORDER_CONTACTS,
  SECONDARY_CONTACTS,
  REFI_FIELDS,
  REFI_SECTION_TITLES,
  PRODUCT_TYPES,
  VESTING_OPTIONS,
  FORM_FIELDS,
  SECTION_TITLES
} from './CaseDataForm/constants';

// Import extracted helpers
import { isValueEmpty, createEmptyContact, searchSoftProContacts, getFIPSCode } from './CaseDataForm/helpers';

// Import modal components
import ApiResponseModal from './CaseDataForm/modals/ApiResponseModal';
import CredentialModal from './CaseDataForm/modals/CredentialModal';
import ErrorModal from './CaseDataForm/modals/ErrorModal';

function CaseDataForm({ caseData, selectedProviders, onProviderToggle, onReset, fileName, orderType, refiInstructions = '', refiSelectedModels = [], treasurerContact = null, deedsContact = null, lenderContact = null, loanOfficerContact = null, processorContact = null, dataTraceAutoResult = null }) {
  const [editedValues, setEditedValues] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Refinance Workflow stages
  const [propertyVerificationStatus, setPropertyVerificationStatus] = useState('pending'); // pending, verifying, verified, needs-correction
  const [dataTraceData, setDataTraceData] = useState(null);
  const [editedDataTraceFields, setEditedDataTraceFields] = useState({}); // Track user edits to DataTrace fields
  const [dataTraceFullResponse, setDataTraceFullResponse] = useState(null);
  const [dataTraceRequestPayload, setDataTraceRequestPayload] = useState(null); // Store full API response
  const [showDataTraceModal, setShowDataTraceModal] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false); // Show tax API response modal
  const [verificationError, setVerificationError] = useState(null); // Store verification errors
  const [showCredentialModal, setShowCredentialModal] = useState(false); // Show credential input modal
  const [secretKeyInput, setSecretKeyInput] = useState(''); // Last 3 characters of secret key

  // Tax search hook
  const {
    taxSearchStatus,
    taxSearchData,
    taxSearchError,
    startTaxSearch,
    checkTaxStatus
  } = useTaxSearch();

  // Title search hook
  const {
    titleSearchStatus,
    titleSearchData,
    titleSearchError,
    startTitleSearch,
    checkTitleStatus
  } = useTitleSearch();

  // Collapsible section state - all expanded by default
  const [collapsedSections, setCollapsedSections] = useState({
    dataExtraction: false,
    refiFields: false, // Refinance additional fields - expanded by default
    comingSoonFields: false, // Coming soon fields - expanded by default
    propertyVerification: false,
    governmentContacts: false, // Expanded by default
    softproSubmit: false,
    taxSearch: false,  // Expanded by default
    titleSearch: false, // Expanded by default
    proformaCommitment: false, // Expanded by default
    finalReview: false  // Expanded by default
  });

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Track expanded state for condensed field views
  const [expandedFields, setExpandedFields] = useState({});

  // Contact state management
  const [governmentContacts, setGovernmentContacts] = useState(
    GOVERNMENT_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [orderContacts, setOrderContacts] = useState(
    ORDER_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [secondaryContacts, setSecondaryContacts] = useState(
    SECONDARY_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [showVillageContact, setShowVillageContact] = useState(false);

  // Track edits to treasurer and deeds contact fields
  const [editedTreasurerContact, setEditedTreasurerContact] = useState(null);
  const [editedDeedsContact, setEditedDeedsContact] = useState(null);
  const [editedLenderContact, setEditedLenderContact] = useState(null);

  // Track selected contacts when multiple options are available
  const [selectedLender, setSelectedLender] = useState(null);
  const [selectedTreasurer, setSelectedTreasurer] = useState(null);
  const [selectedDeeds, setSelectedDeeds] = useState(null);
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState(null);
  const [selectedProcessor, setSelectedProcessor] = useState(null);

  // Track validation failures for required selections
  const [lenderSelectionRequired, setLenderSelectionRequired] = useState(false);

  // Order number scheme toggle ('real' or 'test')
  const [orderNumberScheme, setOrderNumberScheme] = useState('real');

  // Search popup state
  const [searchPopup, setSearchPopup] = useState({
    isOpen: false,
    contactType: null,
    contactId: null,
    searchField: null,
    searchValue: '',
    results: [],
    isLoading: false
  });

  const openSearchPopup = (contactType, contactId, searchField, initialValue = '') => {
    setSearchPopup({
      isOpen: true,
      contactType,
      contactId,
      searchField,
      searchValue: initialValue,
      results: [],
      isLoading: false
    });
  };

  const closeSearchPopup = () => {
    setSearchPopup({
      isOpen: false,
      contactType: null,
      contactId: null,
      searchField: null,
      searchValue: '',
      results: [],
      isLoading: false
    });
  };

  const handleSearchInputChange = (value) => {
    setSearchPopup(prev => ({ ...prev, searchValue: value }));
  };

  const performSearch = async () => {
    setSearchPopup(prev => ({ ...prev, isLoading: true }));

    try {
      const results = await searchSoftProContacts(
        searchPopup.searchField,
        searchPopup.searchValue,
        searchPopup.contactType
      );
      setSearchPopup(prev => ({ ...prev, results, isLoading: false }));
    } catch (error) {
      console.error('Search failed:', error);
      setSearchPopup(prev => ({ ...prev, results: [], isLoading: false }));
    }
  };

  const selectContactFromSearch = (selectedContact) => {
    const { contactType, contactId } = searchPopup;

    const setterMap = {
      government: setGovernmentContacts,
      order: setOrderContacts,
      secondary: setSecondaryContacts
    };

    setterMap[contactType](prev => ({
      ...prev,
      [contactId]: {
        name: selectedContact.name,
        phone: selectedContact.phone,
        email: selectedContact.email
      }
    }));

    closeSearchPopup();
  };

  const handleContactChange = (contactType, contactId, field, value) => {
    const setterMap = {
      government: setGovernmentContacts,
      order: setOrderContacts,
      secondary: setSecondaryContacts
    };

    setterMap[contactType](prev => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        [field]: value
      }
    }));
  };

  const handleFieldEdit = (fieldId, value) => {
    // Auto-fill FIPS code when county changes
    if (fieldId === 'PropertyCounty') {
      const fips = getFIPSCode(value);
      setEditedValues(prev => ({
        ...prev,
        [fieldId]: value,
        'PropertyFIPS': fips
      }));
    } else {
      setEditedValues(prev => ({
        ...prev,
        [fieldId]: value
      }));
    }
  };

  const handleValueSelect = (fieldId, provider) => {
    setSelectedValue(prev => ({
      ...prev,
      [fieldId]: provider
    }));
    setEditedValues(prev => ({
      ...prev,
      [fieldId]: caseData[provider][fieldId]
    }));
  };

  const checkConsensus = (fieldId) => {
    if (selectedProviders.length === 0) return true;
    const values = selectedProviders.map(provider => caseData[provider][fieldId]);

    // DO NOT include DataTrace in consensus check - it's a data source, not an AI model
    // DataTrace is only included in getMostCommonValue() to determine the final value

    // Filter out empty values
    const nonEmptyValues = values.filter(v => !isValueEmpty(v));

    // If all values are empty, return false (no consensus on actual data)
    if (nonEmptyValues.length === 0) return false;

    const uniqueValues = [...new Set(nonEmptyValues)];
    return uniqueValues.length === 1;
  };

  const getMostCommonValue = (fieldId) => {
    if (selectedProviders.length === 0) return '';

    const values = selectedProviders.map(provider => caseData[provider][fieldId]);

    // Special handling for PropertyCounty - include DataTrace result
    if (fieldId === 'PropertyCounty' && dataTraceAutoResult?.property?.county) {
      values.push(dataTraceAutoResult.property.county);
    }

    // Special handling for PropertyAPN - include DataTrace result
    if (fieldId === 'PropertyAPN' && dataTraceAutoResult?.property?.apn) {
      values.push(dataTraceAutoResult.property.apn);
    }

    // Filter out empty/invalid values
    const nonEmptyValues = values.filter(v => !isValueEmpty(v));

    // If all values are empty, return empty string
    if (nonEmptyValues.length === 0) return '';

    const valueCounts = nonEmptyValues.reduce((acc, value) => {
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {});

    const sortedValues = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);
    return sortedValues.length > 0 ? sortedValues[0][0] : '';
  };

  // Auto-fill FIPS code when AI extracts county value
  React.useEffect(() => {
    const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');
    const currentFips = editedValues['PropertyFIPS'] || getMostCommonValue('PropertyFIPS');

    // Only auto-fill if county has a value but FIPS is empty
    if (county && !currentFips) {
      const fips = getFIPSCode(county);
      if (fips) {
        setEditedValues(prev => ({
          ...prev,
          'PropertyFIPS': fips
        }));
      }
    }
  }, [editedValues, selectedProviders, caseData]);

  // Property verification handler
  const handlePropertyVerification = async () => {
    setPropertyVerificationStatus('verifying');
    setVerificationError(null); // Clear previous errors

    try {
      // Check if DataTrace credentials are configured
      if (!isConfigured()) {
        setShowCredentialModal(true);
        setPropertyVerificationStatus('pending');
        return;
      }

      // Get property address components
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');
      const countyFips = editedValues['PropertyFIPS'] || getMostCommonValue('PropertyFIPS');

      // Validate required fields
      if (!address || !city || !state) {
        const error = {
          message: 'Missing required property address fields',
          details: 'Please ensure Property Address, City, and State are filled in before verifying.',
          fields: { address, city, state, zip, county }
        };
        setVerificationError(error);
        setPropertyVerificationStatus('needs-correction');
        return;
      }

      console.log('Verifying property with DataTrace:', { address, city, state, zip, county, countyFips });

      // Call DataTrace API
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        countyFips: countyFips || '',
        apn: (editedValues['PropertyAPN'] || getMostCommonValue('PropertyAPN') || '').trim() // Optional APN for property search
      };

      const dataTraceResponse = await searchPropertyByAPN(searchParams);
      console.log('DataTrace Response:', dataTraceResponse);

      // Check if we got valid data
      if (!dataTraceResponse || !dataTraceResponse.property) {
        throw new Error('Invalid response from DataTrace API');
      }

      // Extract response status and transaction ID from raw API response
      const apiStatus = dataTraceResponse._rawApiResponse?.report_status || 'UNKNOWN';
      const transactionId = dataTraceResponse._rawApiResponse?.transaction_id || 'N/A';

      // Store the raw API response in the same format as transaction history
      // This ensures the modal shows exactly the same data as the API Tester
      const fullApiResponse = {
        data: dataTraceResponse._rawApiResponse,
        metadata: {
          timestamp: new Date().toISOString(),
          apiStatus: apiStatus,
          transactionId: transactionId,
          address: `${address}, ${city}, ${state} ${zip}`,
          productCode: 'property_data',
          productType: 'property_detail'
        }
      };

      setPropertyVerificationStatus('verified');
      setDataTraceData({
        apn: dataTraceResponse.property?.apn || 'N/A',
        address: dataTraceResponse.property?.address || 'N/A',
        legalDescription: dataTraceResponse.property?.legalDescription || 'Not available',
        apiStatus: apiStatus,
        transactionId: transactionId
      });
      setDataTraceFullResponse(fullApiResponse);
      setDataTraceRequestPayload(dataTraceResponse._requestBody);

      // Save transaction to history for auditing (same as API Tester)
      if (dataTraceResponse._rawApiResponse?.transaction_id) {
        try {
          const productConfig = {
            productCode: 'property_data',
            productType: 'property_detail',
            endpoint: 'property_data_sync'
          };
          const transaction = createTransactionRecord(
            searchParams,
            productConfig,
            { data: dataTraceResponse._rawApiResponse },
            dataTraceResponse._requestBody
          );
          await saveTransaction(transaction);
          console.log('Transaction saved to history:', transaction.transactionId);
        } catch (saveError) {
          console.error('Failed to save transaction to history:', saveError);
          // Don't fail the verification if transaction save fails
        }
      }

    } catch (error) {
      console.error('Property verification failed:', error);

      // Parse error message to extract structured error data if available
      let errorData = null;
      try {
        // Try to extract JSON from error message
        const detailsMatch = error.message.match(/--- Details ---\n(.*?)\n--- /s);
        if (detailsMatch) {
          errorData = JSON.parse(detailsMatch[1]);
        }
      } catch (parseError) {
        // If parsing fails, use raw error
      }

      // Check if this is a NotAvailable error
      const isNotAvailable = errorData &&
        errorData.errors &&
        errorData.errors.some(err => typeof err === 'string' && err.includes('NotAvailable'));

      if (isNotAvailable) {
        setVerificationError({
          message: 'Property data not available in DataTrace',
          errorType: 'NotAvailable',
          details: 'DataTrace does not have data for this property in their instant_recordable_legal product database.',
          suggestions: [
            '• Verify the address is correct and complete',
            '• Try using the standalone DataTrace module with different search options',
            '• The property may be new or not yet recorded in DataTrace\'s database',
            '• Consider using an alternative data source (Titlepoint or Accelerate when available)'
          ],
          transactionId: errorData.transaction_id,
          confirmationNumber: errorData.servicing_platform_confirmation_number,
          rawError: error.message,
          timestamp: new Date().toISOString()
        });
      } else {
        // Generic error
        setVerificationError({
          message: 'Property verification failed',
          details: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
      }

      setPropertyVerificationStatus('needs-correction');
    }
  };

  // Handle credential submission
  const handleCredentialSubmit = async (e) => {
    e.preventDefault();

    try {
      const creds = getCredentials();
      const fullSecret = creds.partialSecret + secretKeyInput;

      setCredentials(creds.clientId, fullSecret, creds.scope);
      setShowCredentialModal(false);
      setSecretKeyInput('');

      // Retry verification after credentials are set
      handlePropertyVerification();
    } catch (error) {
      setVerificationError({
        message: 'Failed to configure DataTrace credentials',
        details: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Tax search handler - uses custom hook
  const handleTaxSearch = async () => {
    try {
      // Get property details from verified data
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');

      console.log('Ordering tax search from DataTrace...');

      // Call real DataTrace TaxSource API via hook
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        apn: dataTraceData?.apn // Use APN from property verification if available
      };

      await startTaxSearch(searchParams);
    } catch (error) {
      console.error('Tax search failed:', error);
      // Error state is already managed by the hook
    }
  };

  // Title search handler - uses custom hook
  const handleTitleSearch = async () => {
    try {
      // Get property details from verified data
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');

      console.log('Ordering title search from DataTrace TIQSA...');

      // Call DataTrace TIQSA API via hook
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        apn: dataTraceData?.apn // Use APN from property verification if available
      };

      await startTitleSearch(searchParams, 'current_owner_bronze');
    } catch (error) {
      console.error('Title search failed:', error);
      // Error state is already managed by the hook
      alert('Title search failed: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for unresolved selections BEFORE doing anything else
    const unresolved = [];

    if (lenderContact?.lenders && lenderContact.lenders.length > 1 && !selectedLender) {
      unresolved.push('lender');
    }

    if (treasurerContact?.contacts && treasurerContact.contacts.length > 1 && !selectedTreasurer) {
      unresolved.push('treasurer');
    }

    if (deedsContact?.contacts && deedsContact.contacts.length > 1 && !selectedDeeds) {
      unresolved.push('deeds');
    }

    if (loanOfficerContact?.people && loanOfficerContact.people.length > 1 && !selectedLoanOfficer) {
      unresolved.push('loan officer');
    }

    if (processorContact?.people && processorContact.people.length > 1 && !selectedProcessor) {
      unresolved.push('processor');
    }

    if (unresolved.length > 0) {
      // Set validation flags for visual feedback
      if (unresolved.includes('lender')) {
        setLenderSelectionRequired(true);
      }

      alert(`Please select a contact from the multiple options found for: ${unresolved.join(', ')}`);

      // Expand and scroll to first unresolved selection
      const sectionMap = {
        lender: '.lender-contact-section',
        treasurer: '.government-contacts-section',
        deeds: '.government-contacts-section'
      };

      // Note: We don't need to expand lender section here since it's not collapsible
      // (it doesn't use the collapsedSections state like other workflow sections)

      const firstUnresolvedSection = document.querySelector(sectionMap[unresolved[0]]);
      if (firstUnresolvedSection) {
        firstUnresolvedSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return; // Stop submission
    }

    // Reset states
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      const finalData = {};
      FORM_FIELDS.forEach(field => {
        finalData[field.id] = editedValues[field.id] || getMostCommonValue(field.id);
      });

      // Include refinance fields (borrower and loan data) for refinance orders
      if (orderType === 'refinance') {
        REFI_FIELDS.forEach(field => {
          finalData[field.id] = editedValues[field.id] || getMostCommonValue(field.id);
        });
      }

      // Include contact data
      finalData.contacts = {
        government: governmentContacts,
        order: orderContacts,
        secondary: secondaryContacts
      };

      // Include government contacts from auto-search (treasurer and deeds)
      if (treasurerContact?.contacts || deedsContact?.contacts) {
        finalData.governmentContactsAutoSearch = {
          treasurer: selectedTreasurer || (treasurerContact?.contacts?.length === 1 ? treasurerContact.contacts[0] : null) || editedTreasurerContact || treasurerContact?.contact || null,
          deeds: selectedDeeds || (deedsContact?.contacts?.length === 1 ? deedsContact.contacts[0] : null) || editedDeedsContact || deedsContact?.contact || null
        };
      }

      // Include lender contact from auto-search
      if (lenderContact?.lenders) {
        finalData.lenderAutoSearch = {
          found: lenderContact.found,
          lender: selectedLender || (lenderContact.lenders.length === 1 ? lenderContact.lenders[0] : null) || editedLenderContact || lenderContact.lender
        };
      }

      // Include loan officer contact from auto-search
      if (loanOfficerContact?.people) {
        finalData.loanOfficerAutoSearch = {
          found: loanOfficerContact.found,
          person: selectedLoanOfficer || (loanOfficerContact.people.length === 1 ? loanOfficerContact.people[0] : null)
        };
      }

      // Include processor contact from auto-search
      if (processorContact?.people) {
        finalData.processorAutoSearch = {
          found: processorContact.found,
          person: selectedProcessor || (processorContact.people.length === 1 ? processorContact.people[0] : null)
        };
      }

      // Include refinance-specific data
      if (orderType === 'refinance') {
        finalData.refiInstructions = refiInstructions;
        finalData.selectedModels = refiSelectedModels;
      }

      // Include internal ID from MongoDB if available
      if (caseData.internalId) {
        finalData.internalId = caseData.internalId;
      }

      // Include order number scheme
      finalData.orderNumberScheme = orderNumberScheme;

      console.log('Submitting to SoftPro:', finalData);

      // Submit to SoftPro API
      const response = await submitToSoftPro(finalData);

      console.log('SoftPro Response:', response);
      setSubmitSuccess(true);

      // Show success message with order number from response
      const projectName = `${finalData.PropertyAddress1 || ''}${finalData.PropertyAddress2 ? ' ' + finalData.PropertyAddress2 : ''}`;
      const orderNumber = response.orderNumber || finalData.escrowNumber || 'Auto-generated';
      alert(`Successfully submitted to SoftPro!\n\nProject: ${projectName}\nOrder Number: ${orderNumber}`);

    } catch (error) {
      console.error('Failed to submit to SoftPro:', error);
      setSubmitError(error.message || 'Failed to submit order to SoftPro');
      alert(`Error submitting to SoftPro: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable contact card renderer
  const renderContactCard = (contact, contactData, contactType) => {
    // Skip village if not shown
    if (contact.id === 'village' && !showVillageContact) return null;

    const renderFieldWithSearch = (fieldName, fieldType, placeholder, inputType = 'text') => (
      <div className="contact-field">
        <label>{fieldName}</label>
        <div className="contact-field-input-wrapper">
          <input
            type={inputType}
            value={contactData[contact.id]?.[fieldType] || ''}
            onChange={(e) => handleContactChange(contactType, contact.id, fieldType, e.target.value)}
            placeholder={placeholder}
          />
          <button
            type="button"
            className="search-button"
            onClick={() => openSearchPopup(
              contactType,
              contact.id,
              fieldType,
              contactData[contact.id]?.[fieldType] || ''
            )}
            title={`Search SoftPro by ${fieldName}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
          </button>
        </div>
      </div>
    );

    return (
      <div key={contact.id} className="contact-card">
        <div className="contact-card-header">
          <span className="contact-card-title">{contact.label}</span>
          {!contact.required && <span className="optional-badge">Optional</span>}
        </div>
        <div className="contact-fields">
          {renderFieldWithSearch('Name', 'name', 'Contact name')}
          {renderFieldWithSearch('Phone', 'phone', '(555) 555-5555', 'tel')}
          {renderFieldWithSearch('Email', 'email', 'email@example.com', 'email')}
        </div>
      </div>
    );
  };

  // Search popup modal component
  const renderSearchPopup = () => {
    if (!searchPopup.isOpen) return null;

    const fieldLabels = {
      name: 'Name',
      phone: 'Phone',
      email: 'Email'
    };

    return (
      <div className="search-popup-overlay" onClick={closeSearchPopup}>
        <div className="search-popup" onClick={(e) => e.stopPropagation()}>
          <div className="search-popup-header">
            <h3>Search SoftPro Contacts</h3>
            <button className="search-popup-close" onClick={closeSearchPopup}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="search-popup-content">
            <div className="search-popup-field">
              <label>Search by {fieldLabels[searchPopup.searchField]}</label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={searchPopup.searchValue}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                  placeholder={`Enter ${fieldLabels[searchPopup.searchField].toLowerCase()} to search...`}
                  autoFocus
                />
                <button
                  type="button"
                  className="search-submit-button"
                  onClick={performSearch}
                  disabled={searchPopup.isLoading}
                >
                  {searchPopup.isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="search-results">
              {searchPopup.isLoading && (
                <div className="search-loading">Searching SoftPro database...</div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length === 0 && searchPopup.searchValue && (
                <div className="search-no-results">No contacts found. Try a different search term.</div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length > 0 && (
                <div className="search-results-list">
                  <div className="search-results-header">
                    <span>Select a contact to populate all fields:</span>
                  </div>
                  {searchPopup.results.map((result) => (
                    <div
                      key={result.id}
                      className="search-result-item"
                      onClick={() => selectContactFromSearch(result)}
                    >
                      <div className="search-result-name">{result.name}</div>
                      <div className="search-result-details">
                        <span>{result.phone}</span>
                        <span>{result.email}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length === 0 && !searchPopup.searchValue && (
                <div className="search-hint">
                  Enter a search term and click Search to find contacts in SoftPro.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Special renderer for MaritalStatus toggle (only shown if there are 2 borrowers)
  const renderMaritalStatusToggle = (field) => {
    // Check if there are 2 borrowers (check if Borrower2 has any data)
    const borrower2FirstName = editedValues['Borrower2FirstName'] || getMostCommonValue('Borrower2FirstName');
    const borrower2LastName = editedValues['Borrower2LastName'] || getMostCommonValue('Borrower2LastName');

    // Don't show if only one borrower
    if (!borrower2FirstName && !borrower2LastName) {
      return null;
    }

    const hasConsensus = checkConsensus(field.id);
    const rawValue = editedValues[field.id] !== undefined
      ? editedValues[field.id]
      : getMostCommonValue(field.id);

    const currentValue = rawValue || 'Married'; // Default to Married if not set
    const isExpanded = expandedFields[field.id];
    const isEdited = editedValues[field.id] !== undefined;
    const aiValue = getMostCommonValue(field.id) || '';
    const userModified = isEdited && currentValue !== aiValue;

    return (
      <div
        key={field.id}
        className="condensed-field-group"
        onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
        onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
      >
        <label className="condensed-field-label">
          {field.label}
          {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
        </label>

        {/* Toggle switch for Married/Unmarried */}
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, 'Married')}
              style={{
                padding: '8px 20px',
                border: 'none',
                background: currentValue === 'Married' ? '#667eea' : 'white',
                color: currentValue === 'Married' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === 'Married' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Married
            </button>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, 'Unmarried')}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderLeft: '1px solid #ddd',
                background: currentValue === 'Unmarried' ? '#667eea' : 'white',
                color: currentValue === 'Unmarried' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === 'Unmarried' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Unmarried
            </button>
          </div>
        </div>

        {/* Expandable provider comparison - shown on hover/focus */}
        {isExpanded && selectedProviders.length > 0 && (
          <div className="condensed-provider-comparison">
            {selectedProviders.map(providerId => {
              const provider = AI_PROVIDERS.find(p => p.id === providerId);
              const value = caseData[providerId][field.id];
              const isSelected = selectedValue[field.id] === providerId;

              return (
                <div
                  key={providerId}
                  className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleValueSelect(field.id, providerId);
                  }}
                >
                  <span className="provider-name-small">{provider.name}:</span>
                  <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                    {isValueEmpty(value) ? '(empty)' : value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Condensed field renderer for refinance fields - more compact than standard fields
  const renderCondensedField = (field) => {
    // Special handling for MaritalStatus field
    if (field.id === 'MaritalStatus') {
      return renderMaritalStatusToggle(field);
    }

    // Check if this field should be read-only because it's from SoftPro
    // Note: Only Name and Email fields are from SoftPro - Phone fields remain editable since SoftPro people API doesn't provide phone numbers
    const isLoanOfficerFieldFromSoftPro = (field.id === 'LoanOfficerName' || field.id === 'LoanOfficerEmail') && loanOfficerContact?.found;
    const isProcessorFieldFromSoftPro = (field.id === 'ProcessorName' || field.id === 'ProcessorEmail') && processorContact?.found;
    const isFromSoftPro = isLoanOfficerFieldFromSoftPro || isProcessorFieldFromSoftPro;

    const hasConsensus = checkConsensus(field.id);

    // Check if this field should be pre-filled from DataTrace or SoftPro
    let rawValue = editedValues[field.id] !== undefined
      ? editedValues[field.id]
      : getMostCommonValue(field.id);

    // Override with SoftPro data for loan officer fields (Name and Email only - Phone not provided by SoftPro API)
    if (loanOfficerContact?.found && loanOfficerContact?.people && loanOfficerContact.people.length > 0) {
      const softProOfficer = selectedLoanOfficer || loanOfficerContact.people[0];
      if (field.id === 'LoanOfficerName' && softProOfficer.FullName) {
        rawValue = softProOfficer.FullName;
      } else if (field.id === 'LoanOfficerEmail' && softProOfficer.Email) {
        rawValue = softProOfficer.Email;
      }
      // Note: LoanOfficerPhone is not overridden because SoftPro people API doesn't return phone numbers
    }

    // Override with SoftPro data for processor fields (Name and Email only - Phone not provided by SoftPro API)
    if (processorContact?.found && processorContact?.people && processorContact.people.length > 0) {
      const softProProcessor = selectedProcessor || processorContact.people[0];
      if (field.id === 'ProcessorName' && softProProcessor.FullName) {
        rawValue = softProProcessor.FullName;
      } else if (field.id === 'ProcessorEmail' && softProProcessor.Email) {
        rawValue = softProProcessor.Email;
      }
      // Note: ProcessorPhone is not overridden because SoftPro people API doesn't return phone numbers
    }

    // Pre-fill from DataTrace if available
    if (!rawValue && dataTraceData) {
      if (field.id === 'ParcelID' && dataTraceData.apn) {
        rawValue = dataTraceData.apn;
      } else if (field.id === 'LegalDescription' && dataTraceData.legalDescription) {
        rawValue = dataTraceData.legalDescription;
      }
    }

    const currentValue = rawValue || '';
    const isExpanded = expandedFields[field.id];
    const isEdited = editedValues[field.id] !== undefined;
    const aiValue = getMostCommonValue(field.id) || '';
    const userModified = isEdited && currentValue !== aiValue;
    const isEmpty = !currentValue || currentValue.trim() === '';

    // Determine input class based on consensus and edit state
    let inputClass = 'condensed-input';
    if (isEmpty && !userModified) {
      // Empty fields get yellow highlight as "not found"
      inputClass += ' no-consensus-input';
    } else if (hasConsensus && !userModified) {
      inputClass += ' consensus-input';
    } else if (!hasConsensus && !userModified) {
      inputClass += ' no-consensus-input';
    } else if (userModified) {
      inputClass += ' edited-input';
    }

    return (
      <div
        key={field.id}
        className="condensed-field-group"
        onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
        onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
      >
        <label className="condensed-field-label">
          {field.label}
          {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
          {isFromSoftPro && <span className="softpro-badge" title="From SoftPro - Read-only" style={{ marginLeft: '5px', fontSize: '0.75rem', color: '#059669' }}>🔒 SoftPro</span>}
        </label>

        <div className="condensed-field-input-row">
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
            onFocus={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
            onBlur={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
            className={inputClass}
            placeholder={isEmpty ? "Not Found" : `AI extracted: ${field.label}`}
            readOnly={isFromSoftPro}
            style={isFromSoftPro ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            title={
              isFromSoftPro
                ? "This field is from SoftPro and cannot be edited"
                : userModified
                  ? "Manually edited"
                  : isEmpty
                    ? "AI could not extract this field - please enter manually"
                    : hasConsensus
                      ? "All AI models agree ✓"
                      : "Models disagree - hover to compare"
            }
          />
        </div>

        {/* Expandable provider comparison - shown on hover/focus */}
        {isExpanded && selectedProviders.length > 0 && (
          <div className="condensed-provider-comparison">
            {selectedProviders.map(providerId => {
              const provider = AI_PROVIDERS.find(p => p.id === providerId);
              const value = caseData[providerId][field.id];
              const isSelected = selectedValue[field.id] === providerId;

              return (
                <div
                  key={providerId}
                  className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur on input
                    handleValueSelect(field.id, providerId);
                  }}
                >
                  <span className="provider-name-small">{provider.name}:</span>
                  <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                    {isValueEmpty(value) ? '(empty)' : value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render collapsible workflow section header
  const renderWorkflowSectionHeader = (sectionKey, title, statusBadge = null) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <div
        className="workflow-section-header-clickable"
        onClick={() => toggleSection(sectionKey)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <h3 className="section-title" style={{ marginBottom: 0 }}>
          <span className="collapse-indicator">{isCollapsed ? '▶' : '▼'}</span>
          {title}
          {statusBadge}
        </h3>
      </div>
    );
  };

  // Calculate workflow completion
  const getWorkflowProgress = () => {
    const steps = [
      { name: 'Data Extraction', complete: true, sectionKey: null }, // Always complete if viewing this form
      { name: 'Property Verification', complete: propertyVerificationStatus === 'verified', sectionKey: 'propertyVerification' },
      { name: 'SoftPro (Optional)', complete: submitSuccess, sectionKey: 'softproSubmit' },
      { name: 'Tax Search', complete: taxSearchStatus === 'completed', sectionKey: 'taxSearch' },
      { name: 'Title Search', complete: titleSearchStatus === 'completed', sectionKey: 'titleSearch' }
    ];
    const completedCount = steps.filter(s => s.complete).length;
    return { steps, completedCount, total: steps.length };
  };

  // Navigate to a workflow section
  const navigateToSection = (sectionKey) => {
    if (!sectionKey) return;
    // Expand the section if collapsed
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: false
    }));
    // Scroll to the section with a slight delay to ensure DOM is updated
    setTimeout(() => {
      const section = document.querySelector(`.${sectionKey}-section`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="case-data-form">
      <div className="form-header">
        <button className="back-button-top" onClick={onReset}>← Back</button>
        <div className="form-header-main">
          <div>
            <h2>Review Extracted Case Data from <span className="filename-grey">{fileName}</span></h2>
          </div>
        </div>
        <div className="provider-selector">
          <label>AI Models:</label>
          <div className="provider-toggles">
            {AI_PROVIDERS.map(provider => (
              <button
                key={provider.id}
                className={`provider-toggle ${selectedProviders.includes(provider.id) ? 'active' : ''}`}
                onClick={() => onProviderToggle(provider.id)}
              >
                {provider.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {/* Refinance Workflow Sections - Only show for refinance orders */}
        {orderType === 'refinance' && (
          <>
            {/* Workflow Progress Overview */}
            <div className="workflow-progress-overview">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0, fontSize: '1.125rem' }}>Residential Refinance Workflow</h3>
                <p className="workflow-progress-text" style={{ margin: 0 }}>
                  {getWorkflowProgress().completedCount} of {getWorkflowProgress().total} • Non-sequential
                </p>
              </div>
              <div className="workflow-progress-grid">
                {getWorkflowProgress().steps.map((step, idx) => (
                  <div
                    key={idx}
                    className={`workflow-progress-step ${step.complete ? 'completed' : 'pending'} ${step.sectionKey ? 'clickable' : ''}`}
                    onClick={() => navigateToSection(step.sectionKey)}
                    style={{ cursor: step.sectionKey ? 'pointer' : 'default' }}
                  >
                    <div className="progress-step-indicator">{step.complete ? '✓' : idx + 1}</div>
                    <div className="progress-step-label">{step.name}</div>
                  </div>
                ))}
              </div>
              <div className="workflow-progress-bar">
                <div
                  className="workflow-progress-fill"
                  style={{ width: `${(getWorkflowProgress().completedCount / getWorkflowProgress().total) * 100}%` }}
                />
              </div>
            </div>

            {/* Special Instructions Display */}
            {refiInstructions && (
              <div className="refi-workflow-section">
                <h3 className="section-title">Special Instructions</h3>
                <div className="instructions-display">
                  <pre>{refiInstructions}</pre>
                </div>
              </div>
            )}

            {/* Property Verification Section */}
            <div className="refi-workflow-section property-verification-section propertyVerification-section">
              {renderWorkflowSectionHeader(
                'propertyVerification',
                'Property Verification',
                <>
                  {propertyVerificationStatus === 'verified' && <span className="status-badge status-success">✓ Verified</span>}
                  {propertyVerificationStatus === 'verifying' && <span className="status-badge status-pending">⏳ Verifying...</span>}
                  {propertyVerificationStatus === 'needs-correction' && <span className="status-badge status-warning">⚠ Needs Correction</span>}
                </>
              )}
              {!collapsedSections.propertyVerification && (
                <>
                  <p className="section-description">
                    Compare AI-extracted data against public records from DataTrace API
                  </p>

              <div className="verification-grid">
                <div className="verification-card">
                  <h4>AI Consensus Data</h4>
                  <div className="property-fields-verification">
                    {FORM_FIELDS.filter(f => f.section === 'property').map(field => {
                      const hasConsensus = checkConsensus(field.id);
                      const rawValue = editedValues[field.id] !== undefined
                        ? editedValues[field.id]
                        : getMostCommonValue(field.id);
                      const currentValue = rawValue || '';
                      const isExpanded = expandedFields[field.id];
                      const isEdited = editedValues[field.id] !== undefined;
                      const aiValue = getMostCommonValue(field.id) || '';
                      const userModified = isEdited && currentValue !== aiValue;
                      const isEmpty = !currentValue || currentValue.trim() === '';

                      // Special check for PropertyCounty: are all AI models empty but DataTrace has a value?
                      const isDataTraceOnly = field.id === 'PropertyCounty' &&
                        dataTraceAutoResult?.property?.county &&
                        selectedProviders.every(provider => isValueEmpty(caseData[provider][field.id]));

                      // Determine input class based on consensus and edit state
                      let inputClass = 'condensed-input';
                      if (isEmpty && !userModified) {
                        inputClass += ' no-consensus-input';
                      } else if (isDataTraceOnly && !userModified) {
                        // DataTrace-only state: blue highlight
                        inputClass += ' datatrace-only-input';
                      } else if (hasConsensus && !userModified) {
                        inputClass += ' consensus-input';
                      } else if (!hasConsensus && !userModified) {
                        inputClass += ' no-consensus-input';
                      } else if (userModified) {
                        inputClass += ' edited-input';
                      }

                      return (
                        <div
                          key={field.id}
                          className="condensed-field-group"
                          onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
                          onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
                        >
                          <label className="condensed-field-label">
                            {field.label}
                            {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
                          </label>

                          <div className="condensed-field-input-row">
                            <input
                              type="text"
                              value={currentValue}
                              onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                              onFocus={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
                              onBlur={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
                              className={inputClass}
                              placeholder={isEmpty ? "Not Found" : `AI extracted: ${field.label}`}
                              title={
                                userModified
                                  ? "Manually edited"
                                  : isEmpty
                                    ? "AI could not extract this field - please enter manually"
                                    : isDataTraceOnly
                                      ? "DataTrace provided this value (AI models were empty)"
                                      : hasConsensus
                                        ? "All AI models agree ✓"
                                        : "Models disagree - hover to compare"
                              }
                            />
                          </div>

                          {/* Expandable provider comparison - shown on hover/focus */}
                          {isExpanded && selectedProviders.length > 0 && (
                            <div className="condensed-provider-comparison">
                              {selectedProviders.map(providerId => {
                                const provider = AI_PROVIDERS.find(p => p.id === providerId);
                                const value = caseData[providerId][field.id];
                                const isSelected = selectedValue[field.id] === providerId;

                                return (
                                  <div
                                    key={providerId}
                                    className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleValueSelect(field.id, providerId);
                                    }}
                                  >
                                    <span className="provider-icon">{provider.icon}</span>
                                    <span className="provider-name-small">{provider.name}:</span>
                                    <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                                      {isValueEmpty(value) ? '(empty)' : value}
                                    </span>
                                  </div>
                                );
                              })}
                              {/* Show DataTrace for PropertyCounty field */}
                              {field.id === 'PropertyCounty' && dataTraceAutoResult?.property?.county && (
                                <div
                                  key="datatrace"
                                  className="condensed-provider-value"
                                >
                                  <span className="provider-name-small">DataTrace:</span>
                                  <span className={`value-text ${dataTraceAutoResult.property.county !== currentValue ? 'different' : ''}`}>
                                    {dataTraceAutoResult.property.county}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="verification-card">
                  <h4>Public Records Verification</h4>
                  {dataTraceFullResponse ? (
                    <div className="history-item" style={{ marginTop: '12px' }}>
                      <div className="history-item-header">
                        <div className="history-item-info">
                          <span className="history-status-badge success">
                            Matching Property
                          </span>
                          <span className="history-timestamp">
                            {new Date(dataTraceFullResponse.metadata?.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="history-item-actions">
                          <button
                            type="button"
                            className="history-view-btn"
                            onClick={() => setShowDataTraceModal(true)}
                            title="View request and response payloads"
                          >
                            📄 View Details
                          </button>
                        </div>
                      </div>
                      <div className="history-item-details">
                        <div><strong>Address:</strong> {dataTraceFullResponse.metadata?.address || 'N/A'}</div>
                        <div><strong>Owner:</strong> {dataTraceFullResponse.data?.multiple_properties?.[0]?.owner || dataTraceFullResponse.data?.buyer || 'N/A'}</div>
                        <div><strong>APN:</strong> {dataTraceFullResponse.data?.parcel_id || dataTraceFullResponse.data?.multiple_properties?.[0]?.apn || 'N/A'}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="verification-placeholder">
                      <p>Click "Check DataTrace" below to verify against public records</p>
                    </div>
                  )}

                  <button
                    type="button"
                    className="verify-button"
                    onClick={handlePropertyVerification}
                    disabled={propertyVerificationStatus === 'verifying'}
                    style={{ marginTop: '15px', width: '100%' }}
                  >
                    {propertyVerificationStatus === 'verifying' ? 'Checking...' : 'Check DataTrace'}
                  </button>
                </div>
              </div>
                </>
              )}
            </div>

            {/* Government Contacts Section - County Treasurer and Register of Deeds */}
            {(treasurerContact || deedsContact) && (
              <div className="refi-workflow-section government-contacts-section governmentContacts-section">
                {renderWorkflowSectionHeader(
                  'governmentContacts',
                  'Government Contacts',
                  <>
                    {((treasurerContact && treasurerContact.loading) || (deedsContact && deedsContact.loading)) && <span className="status-badge status-pending">⏳ Searching...</span>}
                    {(!treasurerContact || !treasurerContact.loading) && (!deedsContact || !deedsContact.loading) && ((treasurerContact && treasurerContact.found) || (deedsContact && deedsContact.found)) && <span className="status-badge status-success">✓ Contact Found</span>}
                    {(!treasurerContact || !treasurerContact.loading) && (!deedsContact || !deedsContact.loading) && (!treasurerContact || !treasurerContact.found) && (!deedsContact || !deedsContact.found) && <span className="status-badge status-warning">⚠ Search Failed</span>}
                  </>
                )}
                {!collapsedSections.governmentContacts && (
                  <>
                    <p className="section-description">
                      Government contact information automatically retrieved from SoftPro
                    </p>

                    {/* County Treasurer Contact */}
                    {treasurerContact && (treasurerContact.loading ? (
                      <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="loading-spinner-small"></div>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Searching for County Treasurer contact...</p>
                            <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                              Checking SoftPro database for county contact information
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contacts && treasurerContact.contacts.length > 1 ? (
                      <div className="multi-contact-selection">
                        <p className="selection-prompt">Multiple treasurer contacts found. Please select one:</p>
                        <div className="contact-cards-grid">
                          {treasurerContact.contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className={`contact-selection-card ${selectedTreasurer?.LookupCode === contact.LookupCode ? 'selected' : ''}`}
                              onClick={() => setSelectedTreasurer(contact)}
                            >
                              <div className="contact-card-header">
                                <span>{contact.Name}</span>
                                {selectedTreasurer?.LookupCode === contact.LookupCode && <span className="selected-checkmark">✓</span>}
                              </div>
                              <div className="contact-details">
                                <div><strong>Lookup Code:</strong> {contact.LookupCode}</div>
                                {contact.Address1 && <div><strong>Address:</strong> {contact.Address1}</div>}
                                {contact.City && <div>{contact.City}, {contact.State} {contact.Zip}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contacts && treasurerContact.contacts.length === 1 ? (
                      <div className="contact-card">
                        <div className="contact-card-header">
                          <span className="contact-card-title">County Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={treasurerContact.contacts[0].Name ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={treasurerContact.contacts[0].LookupCode ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contact ? (
                      <div className="contact-card">
                        <div className="contact-card-header">
                          <span className="contact-card-title">County Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={editedTreasurerContact?.Name ?? treasurerContact.contact.Name ?? ''}
                              onChange={(e) => setEditedTreasurerContact(prev => ({
                                ...(prev || treasurerContact.contact),
                                Name: e.target.value
                              }))}
                              placeholder="Contact name"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={editedTreasurerContact?.LookupCode ?? treasurerContact.contact.LookupCode ?? ''}
                              onChange={(e) => setEditedTreasurerContact(prev => ({
                                ...(prev || treasurerContact.contact),
                                LookupCode: e.target.value
                              }))}
                              placeholder="SoftPro lookup code"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                        <p>No County Treasurer contact found</p>
                        <p className="notification-hint">
                          {treasurerContact.error
                            ? `Error: ${treasurerContact.error}`
                            : 'The County Treasurer contact for this property could not be found in SoftPro.'}
                        </p>
                      </div>
                    ))}

                    {/* Register of Deeds Contact */}
                    {deedsContact && (deedsContact.loading ? (
                      <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', marginTop: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="loading-spinner-small"></div>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Searching for Register of Deeds contact...</p>
                            <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                              Checking SoftPro database for register of deeds information
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contacts && deedsContact.contacts.length > 1 ? (
                      <div className="multi-contact-selection" style={{ marginTop: '20px' }}>
                        <p className="selection-prompt">Multiple deeds contacts found. Please select one:</p>
                        <div className="contact-cards-grid">
                          {deedsContact.contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className={`contact-selection-card ${selectedDeeds?.LookupCode === contact.LookupCode ? 'selected' : ''}`}
                              onClick={() => setSelectedDeeds(contact)}
                            >
                              <div className="contact-card-header">
                                <span>{contact.Name}</span>
                                {selectedDeeds?.LookupCode === contact.LookupCode && <span className="selected-checkmark">✓</span>}
                              </div>
                              <div className="contact-details">
                                <div><strong>Lookup Code:</strong> {contact.LookupCode}</div>
                                {contact.Address1 && <div><strong>Address:</strong> {contact.Address1}</div>}
                                {contact.City && <div>{contact.City}, {contact.State} {contact.Zip}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contacts && deedsContact.contacts.length === 1 ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Register of Deeds</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={deedsContact.contacts[0].Name ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={deedsContact.contacts[0].LookupCode ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contact ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Register of Deeds</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={editedDeedsContact?.Name ?? deedsContact.contact.Name ?? ''}
                              onChange={(e) => setEditedDeedsContact(prev => ({
                                ...(prev || deedsContact.contact),
                                Name: e.target.value
                              }))}
                              placeholder="Contact name"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={editedDeedsContact?.LookupCode ?? deedsContact.contact.LookupCode ?? ''}
                              onChange={(e) => setEditedDeedsContact(prev => ({
                                ...(prev || deedsContact.contact),
                                LookupCode: e.target.value
                              }))}
                              placeholder="SoftPro lookup code"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', marginTop: '20px' }}>
                        <p>No Register of Deeds contact found</p>
                        <p className="notification-hint">
                          {deedsContact.error
                            ? `Error: ${deedsContact.error}`
                            : 'The Register of Deeds contact for this property could not be found in SoftPro.'}
                        </p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Lender Contact Section - Lender auto-search */}
            {lenderContact && (
              <div className="refi-workflow-section lender-contact-section">
                <h3 className="section-title">
                  Lender / Mortgagee Information
                  {lenderContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!lenderContact.loading && lenderContact.found && <span className="status-badge status-success">✓ Lender Found</span>}
                  {!lenderContact.loading && !lenderContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Lender information automatically retrieved from SoftPro
                </p>

                {lenderContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for lender contact...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for lender information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lenders && lenderContact.lenders.length > 1 ? (
                  <div className="multi-contact-selection">
                    {lenderSelectionRequired && (
                      <div className="workflow-notification" style={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b', marginBottom: '15px' }}>
                        <p style={{ margin: 0, fontWeight: '700', color: '#92400e', fontSize: '1rem' }}>⚠ Selection Required</p>
                        <p className="notification-hint" style={{ margin: '0.5rem 0 0 0', color: '#92400e' }}>
                          Please select one of the lender contacts below before submitting to SoftPro.
                        </p>
                      </div>
                    )}
                    <p className="selection-prompt">Multiple lender contacts found. Please select one:</p>
                    <div>
                      {lenderContact.lenders.map((lender, idx) => {
                        const isThisSelected = selectedLender && selectedLender._index === idx;
                        return (
                          <div
                            key={`lender-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLender({ ...lender, _index: idx });
                              setLenderSelectionRequired(false);
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {lender.Name} {isThisSelected ? '✓' : ''}
                            </div>
                            <div>Lookup Code: {lender.LookupCode}</div>
                            {lender.Address1 && <div>Address: {lender.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lenders && lenderContact.lenders.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Lender</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Name ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lender ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Lender</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={editedLenderContact?.Name ?? lenderContact.lender.Name ?? ''}
                          onChange={(e) => setEditedLenderContact(prev => ({
                            ...(prev || lenderContact.lender),
                            Name: e.target.value
                          }))}
                          placeholder="Lender name"
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={editedLenderContact?.LookupCode ?? lenderContact.lender.LookupCode ?? ''}
                          onChange={(e) => setEditedLenderContact(prev => ({
                            ...(prev || lenderContact.lender),
                            LookupCode: e.target.value
                          }))}
                          placeholder="SoftPro lookup code"
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No lender contact found</p>
                    <p className="notification-hint">
                      {lenderContact.error
                        ? `Error: ${lenderContact.error}`
                        : 'The lender contact could not be found in SoftPro. Manual lender data will be used.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Loan Officer Contact Section */}
            {loanOfficerContact && (
              <div className="refi-workflow-section loan-officer-contact-section">
                <h3 className="section-title">
                  Loan Officer
                  {loanOfficerContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!loanOfficerContact.loading && loanOfficerContact.found && <span className="status-badge status-success">✓ Found</span>}
                  {!loanOfficerContact.loading && !loanOfficerContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Loan officer information automatically retrieved from SoftPro
                </p>

                {loanOfficerContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for loan officer...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for loan officer information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : loanOfficerContact.found && loanOfficerContact.people && loanOfficerContact.people.length > 1 ? (
                  <div className="multi-contact-selection">
                    <p className="selection-prompt">Multiple loan officers found. Please select one:</p>
                    <div>
                      {loanOfficerContact.people.map((person, idx) => {
                        const isThisSelected = selectedLoanOfficer && selectedLoanOfficer._index === idx;
                        return (
                          <div
                            key={`loan-officer-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLoanOfficer({ ...person, _index: idx });
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {person.FullName} {isThisSelected ? '✓' : ''}
                            </div>
                            {person.Email && <div>Email: {person.Email}</div>}
                            {person.Address1 && <div>Address: {person.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : loanOfficerContact.found && loanOfficerContact.people && loanOfficerContact.people.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Loan Officer</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].FullName ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No loan officer found</p>
                    <p className="notification-hint">
                      {loanOfficerContact.error
                        ? `Error: ${loanOfficerContact.error}`
                        : 'The loan officer could not be found in SoftPro.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Processor Contact Section */}
            {processorContact && (
              <div className="refi-workflow-section processor-contact-section">
                <h3 className="section-title">
                  Processor
                  {processorContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!processorContact.loading && processorContact.found && <span className="status-badge status-success">✓ Found</span>}
                  {!processorContact.loading && !processorContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Processor information automatically retrieved from SoftPro
                </p>

                {processorContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for processor...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for processor information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : processorContact.found && processorContact.people && processorContact.people.length > 1 ? (
                  <div className="multi-contact-selection">
                    <p className="selection-prompt">Multiple processors found. Please select one:</p>
                    <div>
                      {processorContact.people.map((person, idx) => {
                        const isThisSelected = selectedProcessor && selectedProcessor._index === idx;
                        return (
                          <div
                            key={`processor-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProcessor({ ...person, _index: idx });
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {person.FullName} {isThisSelected ? '✓' : ''}
                            </div>
                            {person.Email && <div>Email: {person.Email}</div>}
                            {person.Address1 && <div>Address: {person.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : processorContact.found && processorContact.people && processorContact.people.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Processor</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={processorContact.people[0].FullName ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={processorContact.people[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={processorContact.people[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={processorContact.people[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No processor found</p>
                    <p className="notification-hint">
                      {processorContact.error
                        ? `Error: ${processorContact.error}`
                        : 'The processor could not be found in SoftPro.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Borrower & Loan Information Section */}
            <div className="refi-workflow-section refi-fields-section">
              {renderWorkflowSectionHeader(
                'refiFields',
                'Borrower & Loan Information',
                null
              )}
              {!collapsedSections.refiFields && (
                <>
                  <p className="section-description">
                    AI-extracted borrower and loan details from refinance documents
                  </p>
                  {Object.entries(REFI_SECTION_TITLES).map(([sectionKey, sectionTitle]) => {
                    const sectionFields = REFI_FIELDS.filter(f => f.section === sectionKey);
                    if (sectionFields.length === 0) return null;

                    return (
                      <div key={sectionKey} className="refi-subsection">
                        <h4 className="refi-subsection-title">{sectionTitle}</h4>
                        <div className="condensed-fields-grid">
                          {sectionFields.map(renderCondensedField)}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* SoftPro Section - Combined with Additional Fields */}
            <div className="refi-workflow-section submit-order-section softproSubmit-section">
              {renderWorkflowSectionHeader(
                'softproSubmit',
                'SoftPro',
                submitSuccess && <span className="status-badge status-success">✓ Submitted</span>
              )}
              {!collapsedSections.softproSubmit && (
                <>
                  <button
                    type="submit"
                    className="submit-order-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating Order...' : 'Create Order in SoftPro'}
                  </button>

                  {/* Order Number Scheme Toggle */}
                  <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>Order Number:</span>
                    <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
                      <button
                        type="button"
                        onClick={() => setOrderNumberScheme('real')}
                        style={{
                          padding: '6px 16px',
                          border: 'none',
                          background: orderNumberScheme === 'real' ? '#667eea' : 'white',
                          color: orderNumberScheme === 'real' ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: orderNumberScheme === 'real' ? '600' : '400',
                          transition: 'all 0.2s'
                        }}
                      >
                        Real
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrderNumberScheme('test')}
                        style={{
                          padding: '6px 16px',
                          border: 'none',
                          borderLeft: '1px solid #ddd',
                          background: orderNumberScheme === 'test' ? '#667eea' : 'white',
                          color: orderNumberScheme === 'test' ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: orderNumberScheme === 'test' ? '600' : '400',
                          transition: 'all 0.2s'
                        }}
                      >
                        Test
                      </button>
                    </div>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      ({orderNumberScheme === 'real' ? 'Sequential from DB' : 'YYYYMMDDHHMMSS'})
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Tax Search Section - Always visible for refinance */}
            <div className="refi-workflow-section tax-search-section taxSearch-section">
              {renderWorkflowSectionHeader(
                'taxSearch',
                'Tax Search',
                <>
                  {taxSearchStatus === 'completed' && <span className="status-badge status-success">✓ Complete</span>}
                  {taxSearchStatus === 'pending' && <span className="status-badge status-pending">⏳ In Progress...</span>}
                  {taxSearchStatus === 'needs-review' && <span className="status-badge status-warning">⚠ Needs Review</span>}
                  {taxSearchStatus === 'not-started' && <span className="status-badge">Not Started</span>}
                </>
              )}
              {!collapsedSections.taxSearch && (
                <>
                  <p className="section-description">
                    Tax search via DataTrace TaxSource API - Can run independently or concurrently with title search
                  </p>

                {taxSearchData && !taxSearchData.error && (
                  <div className="tax-search-results">
                    <div className="data-row">
                      <span className="data-label">Tax Year:</span>
                      <span className="data-value">{taxSearchData.taxYear}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Amount:</span>
                      <span className="data-value">{taxSearchData.amount}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Status:</span>
                      <span className={`data-value ${taxSearchData.paid ? 'match-success' : 'match-error'}`}>
                        {taxSearchData.paid ? '✓ Paid' : '✗ Unpaid'}
                      </span>
                    </div>

                    {/* View Full Tax API Response Button */}
                    {taxSearchData.rawResponse && (
                      <div className="verification-api-link" style={{ marginTop: '15px' }}>
                        <button
                          type="button"
                          className="view-api-response-button"
                          onClick={() => setShowTaxModal(true)}
                        >
                          📄 View Full Tax API Response
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {taxSearchData && taxSearchData.error && (
                  <div className="workflow-notification" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                    <p style={{ color: '#856404' }}>⚠ Tax search failed</p>
                    <p className="notification-hint" style={{ color: '#856404' }}>{taxSearchData.errorMessage}</p>
                  </div>
                )}

                {taxSearchStatus === 'pending' && (
                  <div className="workflow-notification">
                    <p>⏳ Waiting for tax search results... This may take minutes, hours, or days.</p>
                    <p className="notification-hint">You will receive a notification when results are ready.</p>
                  </div>
                )}

                  {taxSearchStatus === 'not-started' && (
                    <button
                      type="button"
                      className="order-search-button"
                      onClick={handleTaxSearch}
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Start Tax Search (Temporarily Disabled)
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Title Search Section - Always visible for refinance */}
            <div className="refi-workflow-section title-search-section titleSearch-section">
              {renderWorkflowSectionHeader(
                'titleSearch',
                'Title Search',
                <>
                  {titleSearchStatus === 'completed' && <span className="status-badge status-success">✓ Complete</span>}
                  {titleSearchStatus === 'pending' && <span className="status-badge status-pending">⏳ In Progress...</span>}
                  {titleSearchStatus === 'not-started' && <span className="status-badge">Not Started</span>}
                </>
              )}
              {!collapsedSections.titleSearch && (
                <>
                  <p className="section-description">
                    Title search via DataTrace TIQSA API - Can run independently or concurrently with tax search
                  </p>

                {titleSearchData && (
                  <div className="title-search-results">
                    <div className="workflow-notification" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', marginBottom: '15px' }}>
                      <p style={{ color: '#856404', fontWeight: 'bold' }}>⚠️ DUMMY DATA - Title Search Not Yet Implemented</p>
                      <p className="notification-hint" style={{ color: '#856404' }}>The data below is simulated. Real DataTrace TIQSA API integration is coming soon.</p>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Status:</span>
                      <span className="data-value status-success">✓ {titleSearchData.status}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Documents:</span>
                      <span className="data-value">{titleSearchData.documentsCount} documents retrieved</span>
                    </div>
                  </div>
                )}

                  {titleSearchStatus === 'not-started' && (
                    <button
                      type="button"
                      className="order-search-button"
                      onClick={handleTitleSearch}
                    >
                      Start Title Search
                    </button>
                  )}

                  {titleSearchStatus === 'pending' && (
                    <div className="workflow-notification">
                      <p>⏳ Waiting for title search results... This may take minutes, hours, or days.</p>
                      <p className="notification-hint">You will receive a notification when results are ready.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Pro-Forma Title Commitment Section */}
            <div className="refi-workflow-section proforma-commitment-section">
              {renderWorkflowSectionHeader(
                'proformaCommitment',
                'Pro-Forma Title Commitment',
                null
              )}
              {!collapsedSections.proformaCommitment && (
                <>
                  <p className="section-description">
                    Generate and issue pro-forma title commitment documents
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      View All File Info
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      AI Clear To Go
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Generate Report
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Issue Commitment
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Form actions removed for refinance workflow - use section buttons instead */}
        {orderType !== 'refinance' && (
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onReset} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit to SoftPro'}
            </button>
          </div>
        )}
      </form>

      {/* Search Popup Modal */}
      {renderSearchPopup()}

      {/* DataTrace API Response Modal */}
      <ApiResponseModal
        isOpen={showDataTraceModal && !!dataTraceFullResponse}
        title="DataTrace Property Verification"
        data={dataTraceFullResponse?.data}
        requestPayload={dataTraceRequestPayload}
        transactionInfo={dataTraceFullResponse?.metadata ? {
          transactionId: dataTraceFullResponse.metadata.transactionId,
          status: dataTraceFullResponse.metadata.apiStatus,
          address: dataTraceFullResponse.metadata.address,
          product: `${dataTraceFullResponse.metadata.productCode} / ${dataTraceFullResponse.metadata.productType}`,
          confirmationNumber: dataTraceFullResponse.data?.servicing_platform_confirmation_number,
          timestamp: dataTraceFullResponse.metadata.timestamp
        } : null}
        onClose={() => setShowDataTraceModal(false)}
      />

      {/* Tax API Response Modal */}
      <ApiResponseModal
        isOpen={showTaxModal && taxSearchData && !!taxSearchData.rawResponse}
        title="DataTrace Tax API Response"
        data={taxSearchData?.rawResponse}
        onClose={() => setShowTaxModal(false)}
      />

      {/* DataTrace Credential Configuration Modal */}
      <CredentialModal
        isOpen={showCredentialModal}
        secretKeyInput={secretKeyInput}
        credentials={getCredentials()}
        onSecretKeyChange={setSecretKeyInput}
        onSubmit={handleCredentialSubmit}
        onClose={() => setShowCredentialModal(false)}
      />

      {/* Verification Error Display */}
      <ErrorModal
        error={verificationError}
        onClose={() => setVerificationError(null)}
      />
    </div>
  );
}

export default CaseDataForm;
